import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Synchronises a fixed set of named state values to/from URL search params.
 *
 * - On mount, reads each key from the URL (falling back to the supplied default).
 * - On every change, writes all non-default values back to the URL via
 *   history.replace (no new history entry).
 * - Values that equal their default are omitted from the URL so links stay clean.
 *
 * @param {Record<string, string>} defaults  e.g. { view: 'list', eval: '', tenant: 'all' }
 * @returns {[values: Record<string,string>, set: (keyOrPartial: string|object, value?: string) => void]}
 *
 * @example
 * const [p, setP] = useUrlParams({ view: 'list', eval: '', dateFrom: '' });
 * // current values:  p.view  p.eval  p.dateFrom
 * setP('view', 'dashboard');          // single key
 * setP({ eval: 'escalated', review: '' }); // partial update
 */
export function useUrlParams(defaults) {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultsRef = useRef(defaults); // stable across renders; defaults object should be constant

  // Read initial values from URL on first render only
  const [values, setValues] = useState(() => {
    const init = {};
    for (const [key, def] of Object.entries(defaults)) {
      const raw = searchParams.get(key);
      init[key] = raw !== null ? raw : def;
    }
    return init;
  });

  // Sync state → URL.  We serialise to JSON so the effect dep is a stable string.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const valuesJson = JSON.stringify(values);
  useEffect(() => {
    const p = {};
    const snapshot = JSON.parse(valuesJson);
    const defs = defaultsRef.current;
    for (const [key, val] of Object.entries(snapshot)) {
      if (val !== defs[key]) p[key] = val;
    }
    setSearchParams(p, { replace: true });
  // valuesJson changes whenever any value changes; setSearchParams is stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesJson, setSearchParams]);

  /** Set one key or merge a partial object. */
  const set = useCallback((keyOrPartial, value) => {
    if (typeof keyOrPartial === 'object' && keyOrPartial !== null) {
      setValues(prev => ({ ...prev, ...keyOrPartial }));
    } else {
      setValues(prev => ({ ...prev, [keyOrPartial]: value }));
    }
  }, []);

  return [values, set];
}
